import React, { useState, useCallback, memo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ActivityFilters = memo(({ filters, onFilterChange, onClearFilters, activityTypes }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleTypeToggle = useCallback((type) => {
    let newTypes;
    if (filters.types.includes(type)) {
      newTypes = filters.types.filter(t => t !== type);
    } else {
      newTypes = [...filters.types, type];
    }
    onFilterChange({ types: newTypes });
  }, [filters.types, onFilterChange]);

  const handleSelectAllTypes = useCallback(() => {
    onFilterChange({ types: activityTypes.map(type => type.value) });
  }, [activityTypes, onFilterChange]);

  const handleClearTypes = useCallback(() => {
    onFilterChange({ types: [] });
  }, [onFilterChange]);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  return (
    <div className="bg-surface rounded-lg border border-border">
      <div 
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={toggleCollapsed}
      >
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={18} />
          <h3 className="font-medium text-text-primary">Filter Activities</h3>
        </div>
        <Icon name={isCollapsed ? 'ChevronDown' : 'ChevronUp'} size={18} />
      </div>

      {!isCollapsed && (
        <div className="p-4 border-t border-border">
          {/* Search Filter */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Search
            </label>
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
              <input
                type="text"
                placeholder="Search activities..."
                value={filters.search}
                onChange={(e) => onFilterChange({ search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          {/* Activity Type Filter */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-text-primary">
                Activity Type
              </label>
              <div className="flex space-x-2">
                <button 
                  className="text-xs text-primary hover:text-primary-dark"
                  onClick={handleSelectAllTypes}
                >
                  Select All
                </button>
                <span className="text-text-muted">|</span>
                <button 
                  className="text-xs text-primary hover:text-primary-dark"
                  onClick={handleClearTypes}
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {activityTypes.map((type) => (
                <div 
                  key={type.value}
                  className={`flex items-center p-2 rounded-lg cursor-pointer transition-colors ${
                    filters.types.includes(type.value) 
                      ? 'bg-primary-50 border border-primary' 
                      : 'bg-secondary-50 border border-border hover:border-secondary-300'
                  }`}
                  onClick={() => handleTypeToggle(type.value)}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                    filters.types.includes(type.value) ? 'bg-primary text-white' : 'bg-secondary-100 text-text-secondary'
                  }`}>
                    <Icon name={type.icon} size={14} />
                  </div>
                  <span className={filters.types.includes(type.value) ? 'text-primary font-medium' : 'text-text-secondary'}>
                    {type.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Date Range
            </label>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Start Date</label>
                <DatePicker
                  selected={filters.startDate}
                  onChange={(date) => onFilterChange({ startDate: date })}
                  selectsStart
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  placeholderText="Select start date"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  dateFormat="MMM d, yyyy"
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">End Date</label>
                <DatePicker
                  selected={filters.endDate}
                  onChange={(date) => onFilterChange({ endDate: date })}
                  selectsEnd
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  minDate={filters.startDate}
                  placeholderText="Select end date"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  dateFormat="MMM d, yyyy"
                />
              </div>
            </div>
          </div>

          {/* Clear Filters Button */}
          <Button
            variant="outline"
            fullWidth
            onClick={onClearFilters}
            disabled={!filters.types.length && !filters.search && !filters.startDate && !filters.endDate}
          >
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  );
});

export default ActivityFilters;

